import { capitalize, map as lodashMap, sum as lodashSum, words, isNumber } from 'lodash';
import { G_EmptyStr, USER_TYPE, USER_TYPE_TEXT } from './const';
import { formatToDateTimeMin } from './date';

export function getFullName(
  filePathName: string | null,
  filePathExtension?: string | null
): string {
  return `${filePathName}${filePathExtension ? `.${filePathExtension}` : ''}`;
}

/**
 * 将连接符或者下划线变量转为驼峰格式
 * @param str
 * @returns
 */
export function toCamelCase(str: string) {
  return str.replace(/[-_](\w)/g, (match, p1) => p1.toUpperCase());
}

/**
 * 将连接符或者下划线变量转为驼峰格式并首字母大写
 * @param str
 * @returns
 */
export function ToCamelCaseCapitalize(str: string = '') {
  return toCamelCase(capitalize(str));
}

/**
 * 自定义函数将驼峰命名转换为下划线分隔
 * @param camelCaseString
 * @returns
 */
export function camelToSnake(camelCaseString: string) {
  const wordsArr = words(camelCaseString);
  const snakeCaseString = wordsArr.map((word: string) => word.toLowerCase()).join('_');
  return snakeCaseString;
}

/**
 * 将十进制拆分为移位
 * @param decimal 十进制数
 * @returns 拆分好的位转化成十进制的数组
 * 例如：10（用二进制表示是 110），拆分好的二进制分辨是 100 10，转化成十进制是 8 2，最终返回的是[8, 2]
 */
export function splitDecimalIntoShifts(decimal?: number | null) {
  const result: number[] = [];
  if (decimal === null || decimal === undefined) {
    return result;
  }
  let remaining = +decimal;

  while (remaining > 0) {
    const maxShift = Math.floor(Math.log2(remaining)) + 1;
    const powerOfTwo = 1 << (maxShift - 1);

    if (powerOfTwo <= remaining) {
      result.push(powerOfTwo);
      remaining -= powerOfTwo;
    }
  }

  return result.reverse();
}

export function mergeDecimal(decimal: number[]) {
  return lodashSum(lodashMap(decimal, Number));
}

/**
 * 将后台传入文件大小（字节）格式化成对应的大小
 * @param {string} value - 待校验数据
 * @returns {string} 返回对应大小的字符串
 */
export function formatSize(size: number, unit = 0): String {
  const DIGIT = 2; // 精确到小数点多少位
  const SCALE = 1024; // 文件大小进制
  const UNIT = ['B', 'KB', 'MB', 'GB', 'TB']; // 文件大小单位
  if (!isNumber(unit)) {
    unit = 0;
  }
  const newUnit = unit + 1;
  if (size / SCALE >= 1 && newUnit < UNIT.length) {
    return formatSize(size / SCALE, newUnit);
  }
  return (
    (size + '')
      .split('.')
      .map((v, i) => (i === 1 ? v.slice(0, DIGIT) : v))
      .join('.') +
    ' ' +
    UNIT[unit]
  );
}

export function generateUUID() {
  // API 秘钥是网关与插件接口共用的计费凭据，必须用密码学安全的随机源生成。
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6]! & 0x0f) | 0x40; // version 4
    bytes[8] = (bytes[8]! & 0x3f) | 0x80; // variant 10
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
  }
  // 极老的浏览器没有 Web Crypto；此时生成结果仍会被后端按 UUID 校验，
  // 但随机性不足，仅作为兼容路径。
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

interface AmountOptions {
  commas: boolean;
  currency: string;
  negativeParentheses: boolean;
  fixed: number;
}

// 金额格式化函数实现
export function formatAmount(value: number, options?: Partial<AmountOptions>) {
  // 合并默认选项
  const { commas = true, currency = '', negativeParentheses = false, fixed = 2 } = options || {};
  // 处理无效输入：0 也要带上货币符号与小数位，否则 ¥ 输入框在 0 时会丢掉符号
  if (!value) {
    return currency + (0).toFixed(fixed);
  }

  // 按 fixed 位四舍五入（原实现写死 100，任何 |value| < 0.005 的金额都会被抹成 0，
  // 之后 toFixed 再多位也救不回来）
  const factor = Math.pow(10, fixed);
  let num = Math.round(value * factor) / factor;
  // 处理负数
  const isNegative = num < 0;
  num = Math.abs(num);
  // 格式化为 fixed 位小数
  let parts = num.toFixed(fixed).split('.');
  let integerPart = parts[0] || '';
  const decimalPart = parts[1]; // 添加千分位分隔符
  if (commas) {
    integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  // 构建结果
  let result = `${integerPart}.${decimalPart}`;
  // 添加货币符号
  if (currency) {
    result = currency + result;
  }
  // 处理负数
  if (isNegative) {
    if (negativeParentheses) {
      result = `(${result})`;
    } else {
      result = '-' + result;
    }
  }
  return result;
}

/**
 * 流水金额格式化：按 6 位精度输出，裁掉末尾多余的 0，最少保留 2 位。
 *
 * 按用量计费的单笔扣费常常只有千分之几元（例如 4.91 秒语音识别扣 0.001080）。
 * 消费明细里如果用 2 位小数展示，这些流水会显示成 0.00，看起来像没扣费；
 * 页面上的充值余额则统一按 2 位小数展示（formatAmount 默认值）。
 */
export function formatPreciseAmount(value: number, options?: Partial<AmountOptions>) {
  return formatAmount(value, { ...options, fixed: 6 }).replace(/(\.\d\d[0-9]*?)0+$/, '$1');
}

export function createParticles() {
  const particleCount = 15;
  return Array.from({ length: particleCount }).map((item, index) => {
    // 随机大小
    const size = 2 + Math.random() * 4;
    const sizeStyle = `${size}px`;
    const pStyle = {
      width: sizeStyle,
      height: sizeStyle,
      left: `${Math.random() * 100}%`,
      animationDelay: `${Math.random() * 8}s`,
      animationDuration: `${5 + Math.random() * 5}s`, // 随机动画时长
    };

    return <div className="particle" style={pStyle} key={index}></div>;
  });
}

// 辅助函数：将十六进制颜色转换为RGB
export const hexToRgb = (hex: any) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  // @ts-ignore
  const val = `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
  return result ? val : '79, 172, 254'; // 默认颜色
};

export const isSVIPCheck = (vipLevel: number) => {
  return vipLevel >= 2;
};

export const getVipInfo = (vipLevel: number) => {
  return {
    class: USER_TYPE[vipLevel] || USER_TYPE[0],
    text: USER_TYPE_TEXT[vipLevel] || USER_TYPE_TEXT[0],
  };
};

export const getVipExpireShow = (userInfo: any) => {
  const { vipLevel = 0, vipExpireAt } = userInfo || {};
  return vipLevel > 0 ? formatToDateTimeMin(vipExpireAt) : G_EmptyStr;
};


// 用于判断是否是移动设备
export const isMobile = (): boolean => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
}

// 区分手机和平板
export const isPhone = (): boolean => {
  const userAgent = navigator.userAgent;
  
  // 排除 iPad 和一些大屏设备
  if (/iPad/i.test(userAgent)) return false;
  
  // 判断是否是移动设备
  return /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
}